import { useQueryClient } from "@tanstack/react-query"
import type { RealtimeMessage } from "@zplanem/responses"
import { useCallback } from "react"
import { queryKeys } from "../../lib/query-keys"
import { ticketBoardCacheRoot } from "./ticket-api-paths"
import { usePropertyChannel } from "./use-property-channel"

/**
 * Subscribes the property-wide realtime channel and invalidates the cached
 * queries the server-side events make stale:
 *
 *  - `membership.changed` (invitation accept; future: leave/remove member) →
 *    memberships query. Configured with `staleTime: Infinity`, so without
 *    this hook it would stick to the initial fetch indefinitely.
 *  - `journal.appended` (every property_events insert — emitted by the API's
 *    journal notification dispatcher) → the journal query family. The
 *    `["properties", id, "journal"]` prefix also matches the project-scoped
 *    variants, so one invalidation refreshes the rollup and every open
 *    project journal.
 */
export function usePropertyChannelInvalidators(
	propertyId: string | undefined,
): void {
	const queryClient = useQueryClient()
	const onMessage = useCallback(
		(msg: RealtimeMessage) => {
			if (msg.event === "membership.changed") {
				queryClient.invalidateQueries({
					queryKey: queryKeys.propertyMemberships(msg.payload.propertyId),
				})
				// Also the pending-invitations roster: invitation create/resend emit
				// membership.changed without touching the membership list (ZPL-259).
				queryClient.invalidateQueries({
					queryKey: queryKeys.propertyInvitations(msg.payload.propertyId),
				})
				return
			}
			if (msg.event === "journal.appended") {
				queryClient.invalidateQueries({
					queryKey: queryKeys.propertyJournal(msg.payload.propertyId),
				})
				return
			}
			// A topic was created/updated/deleted — refresh the topic list AND the
			// ticket board (topics regroup tickets), mirroring invalidateTopicConsumers.
			if (msg.event === "topic.changed") {
				queryClient.invalidateQueries({
					queryKey: queryKeys.topics(msg.payload.propertyId),
				})
				queryClient.invalidateQueries({
					queryKey: ticketBoardCacheRoot(msg.payload.propertyId),
				})
				return
			}
			// A project's fields changed — refresh its detail view + the list cards.
			if (msg.event === "project.changed") {
				queryClient.invalidateQueries({
					queryKey: queryKeys.projects(msg.payload.propertyId),
				})
				queryClient.invalidateQueries({
					queryKey: queryKeys.project(
						msg.payload.propertyId,
						msg.payload.projectId,
					),
				})
				return
			}
			// A document appeared/changed/was deleted (ZPL-282) — refresh the
			// property list, the owning project's list, and any open detail.
			if (msg.event === "document.changed") {
				queryClient.invalidateQueries({
					queryKey: queryKeys.propertyDocuments(msg.payload.propertyId),
				})
				if (msg.payload.projectId) {
					queryClient.invalidateQueries({
						queryKey: queryKeys.projectDocuments(
							msg.payload.propertyId,
							msg.payload.projectId,
						),
					})
				}
				return
			}
			// The property was deleted (ZPL-282): evict everything under it and
			// refresh the property list — a refetch would only 404.
			if (msg.event === "property.deleted") {
				queryClient.removeQueries({
					queryKey: queryKeys.property(msg.payload.propertyId),
				})
				queryClient.invalidateQueries({
					queryKey: queryKeys.properties(),
					exact: true,
				})
				return
			}
			// Property content changed — name/description/summaries, incl. the
			// async AI enrichment (ZPL-186). Refresh the detail view + the list.
			if (msg.event === "property.updated") {
				queryClient.invalidateQueries({
					queryKey: queryKeys.property(msg.payload.propertyId),
					exact: true,
				})
				queryClient.invalidateQueries({
					queryKey: queryKeys.properties(),
					exact: true,
				})
			}
		},
		[queryClient],
	)
	usePropertyChannel(propertyId, onMessage)
}
